import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";

import { Button, TextField } from "../../../ui/index.ts";
import { createLoginFormSchema, type LoginFormValues } from "../schemas/index.ts";
import { useLogin } from "../hooks/use-login.ts";
import { formatFormFieldErrors } from "./form-errors.ts";
import styles from "./login-form.module.css";

const DEFAULT_VALUES: LoginFormValues = {
  email: "",
  password: "",
};

interface LoginFormProps {
  allowedEmailDomains: readonly string[];
  emailPlaceholder: string;
}

export function LoginForm({
  allowedEmailDomains,
  emailPlaceholder,
}: LoginFormProps) {
  const { loading, error, submit } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const validationSchema = useMemo(
    () => createLoginFormSchema(allowedEmailDomains),
    [allowedEmailDomains],
  );

  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    validators: { onSubmit: validationSchema },
    onSubmit: async ({ value }) => {
      await submit(value);
    },
  });

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      {error != null ? (
        <p className={`${styles.errorBanner} body-sm`} role="alert">
          {error}
        </p>
      ) : null}

      <form.Field name="email">
        {(field) => (
          <div className={styles.fieldWrapper}>
            <span className={styles.fieldLeadIcon} aria-hidden="true">
              <Mail size={18} />
            </span>
            <TextField
              label="Email address"
              id="login-email"
              type="email"
              autoComplete="email"
              placeholder={emailPlaceholder}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={formatFormFieldErrors(field.state.meta.errors)}
              disabled={loading}
              maxLength={100}
              className={styles.inputElevated}
            />
          </div>
        )}
      </form.Field>

      <form.Field name="password">
        {(field) => (
          <div className={styles.passwordField}>
            <span className={styles.fieldLeadIcon} aria-hidden="true">
              <Lock size={18} />
            </span>
            <TextField
              label="Password"
              id="login-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={formatFormFieldErrors(field.state.meta.errors)}
              disabled={loading}
              className={`${styles.passwordInput} ${styles.inputElevated}`}
            />
            <button
              type="button"
              className={styles.toggleButton}
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              tabIndex={loading ? -1 : 0}
            >
              {showPassword ? (
                <EyeOff size={18} aria-hidden="true" />
              ) : (
                <Eye size={18} aria-hidden="true" />
              )}
            </button>
          </div>
        )}
      </form.Field>

      <div className={styles.actions}>
        <Button
          type="submit"
          fullWidth
          disabled={loading}
          className={styles.submitButton}
          startIcon={
            loading ? <span className={styles.spinner} aria-hidden="true" /> : undefined
          }
        >
          {loading ? "Signing in\u2026" : "Sign in"}
        </Button>

        <Link to="/forgot-password" className={`${styles.forgotLink} body-sm`}>
          Forgot password?
        </Link>
      </div>
    </form>
  );
}
